import { useEffect } from 'react';

// Note: canonical link is handled globally in Layout.js (single source of truth).
// This hook only manages title + meta description + Open Graph + Twitter tags.
//
// Important: it MUST NOT overwrite the values that were pre-rendered at build
// time when no real data is available yet (e.g. seoData still loading from API).
// We therefore only update tags when an actual non-empty value is passed.

export default function useSEO({ 
  title, 
  description, 
  ogImage, 
  url,
}) {
  useEffect(() => {
    // Skip entirely if we have no meaningful data yet — keeps pre-rendered tags intact.
    if (!title && !description && !ogImage) {
      return;
    }

    if (title) {
      document.title = title;
    }

    const updateMetaTag = (property, content, useProperty = false) => {
      if (!content) return; // never write empty values

      const attribute = useProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, property);
        document.head.appendChild(tag);
      }

      tag.setAttribute('content', content);
    };

    updateMetaTag('description', description);

    const ogUrl = url || document.querySelector('link[rel="canonical"]')?.href || window.location.href;
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:url', ogUrl, true);
    updateMetaTag('og:type', 'website', true);

    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
  }, [title, description, ogImage, url]);
}
